import { useEffect, useState } from "react";
import cn from "classnames";
import { Link } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Dialog } from "../Dialog/Dialog";
import { DialogContent, DialogContentAlert, DialogContentButtonFooter } from "../Dialog/DialogContent/DialogContent";
import { Button } from "../Button/Button";
import { useSnackbars } from "../Snackbar/SnackbarsList.hooks";
import { useFeatureFlags } from "../../hooks/useFeatureFlags/useFeatureFlags";
import { storeLocalStorageClient } from "../../views/Viewer/hooks/useViewerState/useViewerState.utils";
import { getNewEventSnackbarData } from "../../views/Viewer/hooks/useNotifyAboutNewEvent/useNotifyAboutNewEvent.utils";
import { PictureConfig, PictureId } from "../../hooks/useFormulaImage/useFormulaImage";
import { SHORTCUTS } from "../../hooks/useHotkeys/useHotkeys.keys";
import { useHotkeys } from "../../hooks/useHotkeys/useHotkeys";
import { Checkbox } from "../Checkbox/Checkbox";
import { useTheme } from "../../hooks/useTheme/useTheme";
import styles from "./DebugPanel.module.scss";
import { debugStore, getLorem } from "./DebugPanel.utils";
import { getSyncDebugSnapshot, SyncDebugSnapshot } from "./syncDebugState";

export const DebugPanel = () => {
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);

  useHotkeys(
    () => ({
      id: "debug",
      allowPropagation: true,
      hotkeys: [
        {
          keys: SHORTCUTS.DEBUG,
          action: () => setIsDebugPanelOpen((d) => !d),
          preventDefault: true,
        },
        {
          keys: SHORTCUTS.CLOSE,
          enabled: isDebugPanelOpen,
          action: () => setIsDebugPanelOpen((d) => !d),
        },
      ],
    }),
    [isDebugPanelOpen],
  );

  return (
    <Dialog isOpen={isDebugPanelOpen} onClose={() => setIsDebugPanelOpen(false)}>
      <DialogContent>
        <DebugPanelContents closePanel={() => setIsDebugPanelOpen(false)} />
      </DialogContent>
    </Dialog>
  );
};

const DebugSyncMonitor = () => {
  const [snapshot, setSnapshot] = useState<SyncDebugSnapshot | null>(null);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setSnapshot(getSyncDebugSnapshot());
    }, 500);

    return () => window.clearInterval(interval);
  }, []);

  if (snapshot == null || snapshot.windows.length === 0) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>Sync Monitor</div>
        <span>No active players</span>
      </div>
    );
  }

  return (
    <div className={cn(styles.section, styles.hasMaxHeight)}>
      <div className={styles.sectionHeader}>Sync Monitor</div>
      <div className={styles.syncTable}>
        <div className={styles.syncHeaderRow}>
          <span>Window</span>
          <span>Drift</span>
          <span>Rate</span>
          <span>PDT Offset</span>
          <span>Res</span>
          <span>Dropped</span>
          <span>Buffer</span>
          <span>BW</span>
        </div>
        {snapshot.windows.map((w) => (
          <div key={w.windowId} className={styles.syncRow}>
            <span className={styles.syncCell}>{w.windowId.slice(0, 8)}</span>
            <span
              className={styles.syncCell}
              style={{ color: Math.abs(w.driftMs) > 100 ? "var(--color-core-red-500)" : undefined }}
            >
              {w.driftMs > 0 ? "+" : ""}
              {w.driftMs}ms
            </span>
            <span className={styles.syncCell}>{w.playbackRate !== 1.0 ? `${w.playbackRate}x` : "1x"}</span>
            <span className={styles.syncCell}>
              {w.manifestOffsetMs != null ? `${w.manifestOffsetMs > 0 ? "+" : ""}${w.manifestOffsetMs}ms` : "—"}
            </span>
            <span className={styles.syncCell}>{w.resolution}</span>
            <span
              className={styles.syncCell}
              style={{ color: w.droppedFrames > 0 ? "var(--color-core-red-500)" : undefined }}
            >
              {w.droppedFrames}
            </span>
            <span className={styles.syncCell}>{w.bufferFullness}s</span>
            <span className={styles.syncCell}>{w.estimatedBandwidth}kbps</span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface DebugPanelContentsProps {
  closePanel: () => void;
}
const DebugPanelContents = (props: DebugPanelContentsProps) => {
  return (
    <>
      <div className={styles.header}>
        <span>Debug options</span>
        <Button variant="Tertiary" iconLeft={XMarkIcon} onClick={props.closePanel} aria-label="Close" />
      </div>
      <DebugSyncMonitor />
      <DebugGeneralSection />
      <DebugColorModeSection />
      <DebugSnackbars />
      <DebugRaceSettings {...props} />
    </>
  );
};

const debugPictureId = PictureId.parse("1000009056-c5aa10c6-c5b7-4cb6-b122-201cbb5b4dc0");
const debugPictureConfig: PictureConfig = { id: debugPictureId, variants: ["landscape_web", "landscape_hero_web"] };
const DebugSnackbars = () => {
  const { openSnackbar, closeSnackbar } = useSnackbars();

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>Snackbars</div>
      <div className={styles.buttonsRow}>
        <Button
          variant="Secondary"
          onClick={() =>
            openSnackbar({
              content: getLorem(),
              title: getLorem(20),
            })
          }
        >
          Lorem ipsum
        </Button>
        <Button
          variant="Secondary"
          onClick={() => {
            const id = openSnackbar(
              getNewEventSnackbarData("Weekend Warm-Up — Austria", "1000007241", debugPictureConfig, () =>
                closeSnackbar(id),
              ),
            );
          }}
        >
          Live event
        </Button>
      </div>
    </div>
  );
};

interface ClearStorageDialogProps {
  onCancel: () => void;
  onConfirm: () => void;
}
const ClearStorageDialog = ({ onCancel, onConfirm }: ClearStorageDialogProps) => {
  return (
    <DialogContent maxWidth={368}>
      <DialogContentAlert
        title={`Are you sure you want to clear localStorage?`}
        subtitle={`You will loose all your settings and layouts`}
      />
      <DialogContentButtonFooter>
        <Button fluid variant="Secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button fluid variant="Primary" onClick={onConfirm}>
          Clear
        </Button>
      </DialogContentButtonFooter>
    </DialogContent>
  );
};

const DebugGeneralSection = () => {
  const [isClearStorageDialogOpen, setIsClearStorageDialogOpen] = useState(false);
  const { resetFlags } = useFeatureFlags();

  return (
    <>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>General</div>
        <div className={styles.buttonsRow}>
          <Button variant="Secondary" onClick={() => setIsClearStorageDialogOpen(true)}>
            Clear localStorage
          </Button>
          <Button variant="Secondary" onClick={resetFlags}>
            Reset flags
          </Button>
          <Button
            variant="Secondary"
            onClick={() => {
              throw new Error("Test Error");
            }}
          >
            Trigger error
          </Button>
        </div>
      </div>

      <Dialog isOpen={isClearStorageDialogOpen} onClose={() => setIsClearStorageDialogOpen(false)}>
        <ClearStorageDialog
          onCancel={() => setIsClearStorageDialogOpen(false)}
          onConfirm={() => {
            localStorage.clear();
            window.location.reload();
          }}
        />
      </Dialog>
    </>
  );
};

const DebugColorModeSection = () => {
  const { setTheme, theme, displayedTheme } = useTheme();
  const toggleDarkMode = () => {
    setTheme(displayedTheme === "dark" ? "light" : "dark");
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>Color Mode (Current: {theme})</div>
      <div className={styles.buttonsRow}>
        <Button variant="Secondary" onClick={() => setTheme("dark")}>
          Set Dark
        </Button>
        <Button variant="Secondary" onClick={() => setTheme("light")}>
          Set Light
        </Button>
        <Button variant="Secondary" onClick={() => setTheme("system")}>
          Set System
        </Button>
        <Button variant="Secondary" onClick={() => toggleDarkMode()}>
          Toggle
        </Button>
      </div>
    </div>
  );
};

const DebugRaceSettings = ({ closePanel }: DebugPanelContentsProps) => {
  const { flags, updateFlag } = useFeatureFlags();

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>Race</div>
      <div className={styles.buttonsRow}>
        <Button variant={"Secondary"} as={Link} to="/race/__DEBUG__" onClick={closePanel}>
          Open debug live stream
        </Button>
        <Button
          variant={"Secondary"}
          onClick={() => {
            storeLocalStorageClient.set(debugStore);
            window.location.reload();
          }}
        >
          Load debug store
        </Button>
      </div>
      <Checkbox
        label="Increase background contrast"
        checked={flags.increaseBackgroundContrast}
        onChange={updateFlag("increaseBackgroundContrast")}
      />
      <Checkbox
        label="Show window borders"
        checked={flags.showWindowBorders}
        onChange={updateFlag("showWindowBorders")}
      />
      <Checkbox label="Never hide UI" checked={flags.forceUiVisibility} onChange={updateFlag("forceUiVisibility")} />
      <Checkbox
        label="Disable live notifications"
        checked={flags.disableLiveNotifications}
        onChange={updateFlag("disableLiveNotifications")}
      />
      <Checkbox
        label="Force F1 TV Access subscription"
        checked={flags.forceTVAccess}
        onChange={updateFlag("forceTVAccess")}
      />
      <Checkbox label="Show Live Timing" checked={flags.showLiveTiming} onChange={updateFlag("showLiveTiming")} />
    </div>
  );
};
